import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { bt } from '../i18n';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, ChevronRightIcon, PlusCircleIcon, SearchIcon } from '../ui/icons';

/**
 * AddPopover — the shared "Add section / Add block" picker (Claude Design parity, founder feedback
 * 2026-06-25). A FLOATING, focus-contained dialog popover: search + COLLAPSIBLE, icon'd categories,
 * optionally with a preview pane.
 *
 * There is NO "Apps" tab: the marketplace ships no section/block providers, so the toggle only ever led to
 * an empty state (founder, 2026-09-16). The single remaining source needs no chooser — the list IS the tab.
 *
 * Why a PORTAL (not the old in-flow / absolute panel): the left rail is `overflow:hidden`, so an
 * absolutely-positioned panel was CLIPPED by the rail and could never float over the canvas like Shopify's
 * popup (the founder's complaint). We render the panel into `document.body` and position it `fixed`,
 * anchored to the trigger via `getBoundingClientRect` (RTL-aware; flips above when there's no room below;
 * clamps into the viewport). No shell ancestor establishes a containing block (no transform/filter), so
 * `position:fixed` resolves against the viewport and escapes the rail clip.
 *
 * Accessibility: the panel is `role="dialog" aria-modal="true"` with Tab/Shift+Tab CONTAINED inside it
 * (no focus escaping behind the floating card), search focused on open, focus restored to the trigger on
 * every close path (Esc / outside-click-from-inside / choose). Items are native `<button>`s in a labelled
 * `role="list"` (no false `role="menu"` keyboard contract).
 *
 * jsdom-safe: `getBoundingClientRect` returns zeros under test, which just pins the panel at 0,0 — the
 * dialog/list roles, the category headers and the item buttons stay queryable through the portal.
 */
export interface AddPopoverItem {
  type: string;
  label: string;
  /** Pre-rendered glyph (section/block icon), list-size. */
  icon?: ReactNode;
  /** Larger glyph for the preview pane (falls back to `icon`). */
  previewIcon?: ReactNode;
  /**
   * Extra words the SEARCH matches, beside the visible label (28/09/2026).
   *
   * The list is translated, so the search was too — and only the translated label was matched. A French
   * builder answered "countdown", "timer", "slideshow" or "hero" with « Aucun résultat », although the
   * section was right there under its French name. Sellers type the English word constantly: it is what
   * Shopify calls the section, it is what the theme's own docs call it, and it is what the founder typed
   * the day the countdown section shipped. The English label and the type id go in here.
   */
  keywords?: string;
  /** Disabled (e.g. block at its `max`); shown greyed, not selectable. */
  disabled?: boolean;
  disabledReason?: string;
}
export interface AddPopoverGroup {
  group: string;
  items: AddPopoverItem[];
}

const PANEL_HEIGHT = 420;
const VIEWPORT_MARGIN = 8;

/** Effective panel width = the declared width clamped to the viewport (mirrors the CSS max-width cap). */
function effectiveWidth(declared: number): number {
  return Math.min(declared, window.innerWidth - 2 * VIEWPORT_MARGIN);
}

function computePosition(
  trigger: HTMLElement,
  panelWidth: number,
  panelHeight: number,
  align: 'start' | 'center',
): { top: number; left: number } {
  const r = trigger.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const rtl = getComputedStyle(trigger).direction === 'rtl';

  let left: number;
  if (align === 'center') left = r.left + r.width / 2 - panelWidth / 2;
  else if (rtl)
    left = r.right - panelWidth; // anchor the panel's end edge to the trigger's end edge
  else left = r.left;
  left = Math.min(left, vw - panelWidth - VIEWPORT_MARGIN);
  left = Math.max(VIEWPORT_MARGIN, left);

  let top = r.bottom + 4;
  // Flip above the trigger when the panel would overflow the bottom of the viewport.
  if (top + panelHeight > vh - VIEWPORT_MARGIN) {
    const above = r.top - panelHeight - 4;
    top =
      above >= VIEWPORT_MARGIN
        ? above
        : Math.max(VIEWPORT_MARGIN, vh - panelHeight - VIEWPORT_MARGIN);
  }
  return { top, left };
}

/** A collapsible category section inside the picker (Shrine/Shopify-style grouped, chevron header). */
function AddPopoverCategory({
  group,
  items,
  activeType,
  forceOpen,
  onHover,
  onChoose,
}: {
  group: string;
  items: AddPopoverItem[];
  activeType: string | null;
  forceOpen: boolean;
  onHover: (type: string) => void;
  onChoose: (type: string, disabled?: boolean) => void;
}) {
  const [open, setOpen] = useState(true);
  const expanded = forceOpen || open;
  return (
    <div className="bld-addmenu__group">
      <button
        type="button"
        className="bld-addmenu__grouphead"
        aria-expanded={expanded}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="bld-addmenu__groupchev dir-flip" aria-hidden="true">
          {expanded ? <ChevronDownIcon size={13} /> : <ChevronRightIcon size={13} />}
        </span>
        {group}
      </button>
      {expanded &&
        items.map((it) => (
          <button
            key={it.type}
            type="button"
            disabled={it.disabled}
            title={it.disabled ? it.disabledReason : undefined}
            className={it.type === activeType ? 'bld-addmenu__item is-active' : 'bld-addmenu__item'}
            onClick={() => onChoose(it.type, it.disabled)}
            onMouseEnter={() => onHover(it.type)}
            onFocus={() => onHover(it.type)}
          >
            {it.icon && (
              <span className="bld-addmenu__item-icon" aria-hidden="true">
                {it.icon}
              </span>
            )}
            <span className="bld-addmenu__item-name">{it.label}</span>
          </button>
        ))}
    </div>
  );
}

export function AddPopover({
  groups,
  onChoose,
  triggerLabel,
  triggerClassName = 'bld-add',
  triggerAriaLabel,
  searchPlaceholder,
  menuAriaLabel,
  emptyText,
  previewHint = bt('chrome.add.hover_hint', 'Hover an item to preview it.'),
  twoPane = false,
  renderPreview,
  align = 'start',
  triggerDisabled = false,
  triggerTitle,
}: {
  groups: AddPopoverGroup[];
  onChoose: (type: string) => void;
  triggerLabel: string;
  triggerClassName?: string;
  triggerAriaLabel?: string;
  /** Grey the trigger without unmounting it (superadmin read-only consultation). */
  triggerDisabled?: boolean;
  triggerTitle?: string;
  searchPlaceholder: string;
  /** Accessible name on the dialog + list, e.g. "Add a section". */
  menuAriaLabel: string;
  /** Empty-search copy; "{q}" is replaced with the query. */
  emptyText: string;
  previewHint?: string;
  /** Show the right-hand preview pane (Add-block); single-column when false (Add-section). */
  twoPane?: boolean;
  /**
   * Render a rich LIVE preview of a type in the preview pane instead of the default big glyph (Add-section
   * passes a scaled section render). The type is DEBOUNCED so sweeping the list doesn't mount a heavy tree
   * per row. Requires `twoPane`. Omitted = the icon+label fallback (Add-block).
   */
  renderPreview?: (type: string) => ReactNode;
  /** Anchor alignment relative to the trigger (the canvas '+' centres). */
  align?: 'start' | 'center';
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hovered, setHovered] = useState<string | null>(null);
  // The type whose LIVE preview is actually mounted — trails `previewType` by a debounce so a fast sweep
  // over the list doesn't mount/unmount a full section render on every row (the list highlight + the
  // preview LABEL stay on the immediate `previewType` for snappy feedback).
  const [deferredType, setDeferredType] = useState<string | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const DECLARED_WIDTH = twoPane ? 760 : 340;

  function close(restoreFocus: boolean) {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }

  // Position the floating panel before paint (no flicker), then keep it anchored on scroll/resize.
  // Coalesce bursts into one reposition per frame; ignore the panel's OWN internal list scroll.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    let raf = 0;
    const reposition = () => {
      if (!triggerRef.current) return;
      const h = panelRef.current?.offsetHeight || PANEL_HEIGHT;
      setPos(computePosition(triggerRef.current, effectiveWidth(DECLARED_WIDTH), h, align));
    };
    const onScroll = (e: Event) => {
      if (panelRef.current?.contains(e.target as Node)) return; // panel's own scroll never moves the trigger
      raf ||= requestAnimationFrame(() => {
        raf = 0;
        reposition();
      });
    };
    const onResize = () => {
      raf ||= requestAnimationFrame(() => {
        raf = 0;
        reposition();
      });
    };
    reposition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, DECLARED_WIDTH, align]);

  // Dismiss + focus management. Outside-mousedown (trigger OR panel) closes; restore focus to the trigger
  // only when it was inside the panel. Escape closes + restores. Tab/Shift+Tab are CONTAINED in the panel.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setHovered(null);
      setDeferredType(null);
      return;
    }
    searchRef.current?.focus();
    function focusables(): HTMLElement[] {
      if (!panelRef.current) return [];
      return Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
    }
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      const focusWasInside = panelRef.current?.contains(document.activeElement);
      close(!!focusWasInside);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close(true);
        return;
      }
      if (e.key === 'Tab') {
        const els = focusables();
        if (els.length === 0) return;
        const first = els[0]!;
        const last = els[els.length - 1]!;
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && (active === first || !panelRef.current?.contains(active))) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const q = query.trim().toLowerCase();
  const filteredGroups = groups
    .map((g) => ({
      group: g.group,
      items: g.items.filter(
        (it) => it.label.toLowerCase().includes(q) || (it.keywords ?? '').toLowerCase().includes(q),
      ),
    }))
    .filter((g) => g.items.length > 0);

  const flatItems = filteredGroups.flatMap((g) => g.items);
  const previewType =
    hovered ?? flatItems.find((it) => !it.disabled)?.type ?? flatItems[0]?.type ?? null;
  const preview = flatItems.find((it) => it.type === previewType) ?? null;

  // Debounce the type fed to the (heavy) live preview render. Highlight/label track `previewType`
  // immediately; the mounted section render settles ~90ms after the pointer stops on a row.
  useEffect(() => {
    if (!renderPreview) return;
    const t = window.setTimeout(() => setDeferredType(previewType), 90);
    return () => window.clearTimeout(t);
  }, [previewType, renderPreview]);

  function choose(type: string, disabled?: boolean) {
    if (disabled) return;
    onChoose(type);
    close(true);
  }

  const panelClass =
    `bld-addmenu__panel bld-addmenu__panel--portal` +
    (twoPane ? ' bld-addmenu__panel--twopane' : '');

  return (
    <div className="bld-addmenu">
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={triggerAriaLabel}
        disabled={triggerDisabled}
        title={triggerTitle}
        onClick={() => (open ? close(false) : setOpen(true))}
      >
        <span className="bld-add__icon" aria-hidden="true">
          <PlusCircleIcon size={16} />
        </span>
        {triggerLabel}
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={menuAriaLabel}
            className={panelClass}
            style={{
              top: pos.top,
              left: pos.left,
              maxHeight: Math.min(PANEL_HEIGHT, window.innerHeight - 16),
            }}
          >
            {/* LEFT pane: search + segmented toggle + scrollable categorised list. */}
            <div className="bld-addmenu__list-pane">
              <div className="bld-addmenu__search">
                <span className="bld-addmenu__searchicon" aria-hidden="true">
                  <SearchIcon size={15} />
                </span>
                <input
                  ref={searchRef}
                  type="text"
                  className="bld-addmenu__input"
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <div className="bld-addmenu__scroll" role="list" aria-label={menuAriaLabel}>
                {filteredGroups.length === 0 ? (
                  <p className="bld-addmenu__empty">{emptyText.replace('{q}', query)}</p>
                ) : (
                  filteredGroups.map((g) => (
                    <AddPopoverCategory
                      key={g.group}
                      group={g.group}
                      items={g.items}
                      activeType={previewType}
                      forceOpen={q.length > 0}
                      onHover={setHovered}
                      onChoose={choose}
                    />
                  ))
                )}
              </div>
            </div>
            {/* RIGHT pane: a preview of the hovered / candidate item (Add-block two-pane only). */}
            {twoPane && (
              <div className="bld-addmenu__preview" aria-hidden="true">
                {preview ? (
                  <>
                    <div
                      className={
                        renderPreview
                          ? 'bld-addmenu__preview-canvas bld-addmenu__preview-canvas--live'
                          : 'bld-addmenu__preview-canvas'
                      }
                    >
                      {renderPreview ? (
                        renderPreview(deferredType ?? previewType ?? preview.type)
                      ) : (
                        <span className="bld-addmenu__preview-icon">
                          {preview.previewIcon ?? preview.icon}
                        </span>
                      )}
                    </div>
                    <p className="bld-addmenu__preview-name">{preview.label}</p>
                  </>
                ) : (
                  <p className="bld-addmenu__preview-empty">{previewHint}</p>
                )}
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
